import React, { useState } from 'react';
import {
  Home,
  FileText,
  Calendar,
  Briefcase,
  LifeBuoy,
  UserCircle,
  LayoutDashboard,
  Layers,
  Building,
  Mail,
  Users,
  TicketCheck,
  CalendarDays,
  MoreHorizontal,
  X,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';

export interface TabConfig {
  id: string;
  label: string;
  icon: LucideIcon;
  badgeCount?: number;
  category?: string;
  /**
   * Optional collapsible sidebar group name (admin sidebar redesign,
   * Oct 2026). Consecutive tabs sharing a group render under one
   * expand/collapse header in CollapsibleSidebar. Tabs without a group
   * render flat, exactly as before — other roles never set this.
   */
  group?: string;
}

interface BottomNavProps {
  tabs: TabConfig[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  /**
   * Optional overflow tabs (§2.7 mobile parity): when non-empty, a "More"
   * button joins the bar and opens a bottom sheet listing these tabs, so
   * every sidebar surface stays reachable on phones without crowding the
   * bar. Additive — roles that don't pass it render exactly as before.
   */
  overflowTabs?: TabConfig[];
}

/**
 * Instagram-style fixed bottom navigation bar.
 *
 * - Fixed at bottom with safe-area inset support
 * - 64px height + env(safe-area-inset-bottom)
 * - Each tab: icon centered above label (11px, semibold)
 * - Active: brand blue (#2563eb), stroke-width 2.5
 * - Inactive: slate-500 (#64748b, WCAG AA on white — APP-06), stroke-width 1.5
 * - Active tab has a 2px blue top-border indicator line
 * - tap feedback: active:scale-95
 * - Hidden on screens ≥ 768px (tablet/desktop)
 * - Accessible: role="nav", aria-label, aria-current on active tab
 */
export default function BottomNav({ tabs, activeTab, onTabChange, overflowTabs }: BottomNavProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const hasOverflow = !!overflowTabs && overflowTabs.length > 0;
  const overflowActive = hasOverflow && overflowTabs!.some(t => t.id === activeTab);

  const tabButton = (tab: TabConfig) => {
    const isActive = activeTab === tab.id;
    const Icon = tab.icon;
    return (
      <button
        key={tab.id}
        onClick={() => onTabChange(tab.id)}
        className="flex flex-col items-center justify-center flex-1 py-2 min-h-[48px] gap-0.5 transition-colors active:scale-95 relative cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 rounded-lg"
        style={{ WebkitTapHighlightColor: 'transparent' }}
        aria-current={isActive ? 'page' : undefined}
        aria-label={tab.label}
      >
        {isActive && (
          <div className="absolute top-0 left-2 right-2 h-[3px] btn-primary rounded-b-full" aria-hidden="true" />
        )}
        <div className="relative">
          <Icon
            className={`transition-all duration-150 ${
              isActive ? 'text-brand' : 'text-slate-500'
            }`}
            size={isActive ? 24 : 22}
            strokeWidth={isActive ? 2.5 : 1.5}
            aria-hidden="true"
          />
          {tab.badgeCount !== undefined && tab.badgeCount > 0 && (
            <span
              className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold leading-none shadow-sm"
              aria-label={`${tab.badgeCount > 99 ? '99+' : tab.badgeCount} notifications`}
            >
              {tab.badgeCount > 99 ? '99+' : tab.badgeCount}
            </span>
          )}
        </div>
        <span
          className={`text-[11px] font-semibold tracking-tight transition-colors duration-150 ${
            isActive ? 'text-brand' : 'text-slate-500'
          }`}
        >
          {tab.label}
        </span>
      </button>
    );
  };

  return (
    <>
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex items-stretch md:hidden shadow-[0_-1px_6px_rgba(0,0,0,0.06)]"
      style={{ zIndex: 'var(--z-nav)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 4px)' }}
      aria-label="Primary navigation"
    >
      {tabs.map(tabButton)}
      {hasOverflow && (
        <button
          onClick={() => setMoreOpen(true)}
          className="flex flex-col items-center justify-center flex-1 py-2 min-h-[48px] gap-0.5 transition-colors active:scale-95 relative cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 rounded-lg"
          style={{ WebkitTapHighlightColor: 'transparent' }}
          aria-current={overflowActive ? 'page' : undefined}
          aria-label="More"
        >
          {overflowActive && (
            <div className="absolute top-0 left-2 right-2 h-[3px] btn-primary rounded-b-full" aria-hidden="true" />
          )}
          <MoreHorizontal
            className={`transition-all duration-150 ${overflowActive ? 'text-brand' : 'text-slate-500'}`}
            size={overflowActive ? 24 : 22}
            strokeWidth={overflowActive ? 2.5 : 1.5}
            aria-hidden="true"
          />
          <span className={`text-[11px] font-semibold tracking-tight transition-colors duration-150 ${overflowActive ? 'text-brand' : 'text-slate-500'}`}>
            More
          </span>
        </button>
      )}
    </nav>

    {/* Overflow sheet — every remaining surface, one tap away (§2.7) */}
    {hasOverflow && moreOpen && (
      <div className="fixed inset-0 md:hidden" style={{ zIndex: 'calc(var(--z-nav) + 10)' }}>
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={() => setMoreOpen(false)} />
        <div
          className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl max-h-[75dvh] overflow-y-auto animate-fade-in"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
          role="dialog"
          aria-label="More navigation"
        >
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <p className="text-sm font-bold text-slate-900">More</p>
            <button
              onClick={() => setMoreOpen(false)}
              className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="px-2 pb-2">
            {overflowTabs!.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => { setMoreOpen(false); onTabChange(tab.id); }}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-left transition cursor-pointer ${isActive ? 'bg-blue-50' : 'hover:bg-slate-50 active:bg-slate-100'}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className={`p-2 rounded-xl shrink-0 ${isActive ? 'bg-white text-brand shadow-sm' : 'bg-slate-100 text-slate-500'}`}>
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className={`flex-1 text-sm font-semibold ${isActive ? 'text-brand' : 'text-slate-800'}`}>{tab.label}</span>
                  {tab.badgeCount !== undefined && tab.badgeCount > 0 && (
                    <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                      {tab.badgeCount > 99 ? '99+' : tab.badgeCount}
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    )}
    </>
  );
}
